/**
 * Unified Feedback widget loader.
 *
 * Every Unified system reports into one central feedback backlog; the widget
 * (a shadow-root launcher + panel served by the feedback platform) is mounted
 * once for the whole authenticated app from Shell.tsx. Identity comes from
 * GET /api/v1/feedback/token — same-origin, so the session cookie travels via
 * the nginx /api proxy.
 *
 * Failure policy: silent. A broken widget must never break the app, so every
 * step is guarded and failures only reach the console.
 */

const FEEDBACK_ORIGIN =
  'https://unified-feedback-api.icysand-64c3ecb3.uaenorth.azurecontainerapps.io';

interface UnifiedFeedbackGlobal {
  version?: string;
  init: (options: {
    appId: string;
    env: string;
    endpoint: string;
    tokenUrl: string;
    locale: 'en' | 'ar';
    onError?: (error: unknown) => void;
  }) => void;
}

declare global {
  interface Window {
    UnifiedFeedback?: UnifiedFeedbackGlobal;
  }
}

let started = false;

/**
 * Load + initialise the widget exactly once. Safe to call on every render —
 * subsequent calls are no-ops (a second init would render a second launcher).
 *
 * The widget only supports en/ar; the RTL languages map to ar, the rest to en.
 */
export function initFeedbackWidget(language: string): void {
  if (started) return;
  started = true;

  try {
    const script = document.createElement('script');
    script.src = `${FEEDBACK_ORIGIN}/w/v1.js`;
    script.async = true;
    script.onload = () => {
      try {
        window.UnifiedFeedback?.init({
          appId: 'ured-admin-hub',
          env: 'prod',
          endpoint: FEEDBACK_ORIGIN,
          tokenUrl: '/api/v1/feedback/token',
          locale: language === 'ar' || language === 'ur' ? 'ar' : 'en',
          onError: (e) => console.warn('[feedback]', e),
        });
      } catch (e) {
        console.warn('[feedback] init failed', e);
      }
    };
    script.onerror = () => console.warn('[feedback] widget script failed to load');
    document.head.appendChild(script);
  } catch (e) {
    console.warn('[feedback] setup failed', e);
  }
}
